import React, { useState } from 'react';
import _ from 'lodash';
import { DocumentProvider } from './document';
import AttachmentCarousel from './AttachmentCarousel';
import type {
  TextCollectionParams,
  FormField,
  TextCollectionField,
} from '@scale/shared/types/params/textcollection';
import DiffTree from './DiffTree';
// TODO: Import @material-ui from design-system folder
/* eslint-disable restrict-imports/restrict-imports */
import { makeStyles, useMediaQuery } from '@material-ui/core';
import {
  TextCollectionGraderDiffResponse,
  GraderResult,
  isFieldSetGraderAnnotation,
  GraderValue,
  TextCollectionGraderAnnotation,
} from '@scale/shared/grader/descriptions/textCollection/graderAnnotationType';
import { isFormFields } from '@scale/shared/textcollection/field';
import FormFieldNav from './FormFieldNav';
import { getCurrentFields, Breadcrumb, pathJoin } from './breadcrumbs';
import { TextCollectionUnitValueType } from '@scale/shared/types/response/textcollection';
import { DiffView } from '@scale/shared/types/training';
import { captureMessage } from '@scale/frontend-lib/errors';
import { MOBILE_THRESHOLD } from '@scale/frontend-lib/util';
import classNames from 'classnames';
import { Button } from 'react-scale/components/design-system/Form';

interface TextCollectionAnswerKeyProps {
  params: TextCollectionParams;
  graderDiffResponse: TextCollectionGraderDiffResponse;
  feedbackComment?: string;
  responseKey?: string;
  loggedUserId?: string;
  useProtectedText?: boolean;
  project?: string;
  passedChallenge?: boolean;
}

const TextCollectionAnswerKey: React.FC<TextCollectionAnswerKeyProps> = props => {
  const classes = useAnswerKeyStyles(props);
  const { fields = [], attachments = [], ocrTextFragments = [] } = props.params;
  if (!props.graderDiffResponse?.annotations) {
    captureMessage('Text Collection: graderDiffResponse is nil or has no annotations', {
      userId: props.loggedUserId,
    });
  }
  const annotations = props.graderDiffResponse?.annotations || {};

  const [viewingAnswerKey, setViewingAnswerKey] = useState<boolean>(true);
  const mobile = useMediaQuery(`(max-width: ${MOBILE_THRESHOLD})`);

  const inaccurateForms = fields.filter(form => isInaccurateForm(form, annotations));
  const [activeForm, setActiveForm] = React.useState(() => {
    return (inaccurateForms[0] || fields[0]).field_id;
  });
  const currentBreadcrumb = { id: activeForm, index: 0 };
  const setBreadcrumbs = (breadcrumbs: Breadcrumb[]) => {
    setActiveForm(breadcrumbs[0].id);
  };

  const { parentField } = getCurrentFields(fields, [currentBreadcrumb]);
  const isShownAsMiniform = isFormFields(_.compact([parentField])) && isFormFields(fields);
  const activeFormForAttachment = isShownAsMiniform ? activeForm : '';

  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  // Stacked: Scroll to top if the active form changes.
  React.useEffect(() => {
    if (activeForm && scrollRef.current) {
      scrollRef.current.scroll(0, 0);
    }
  }, [activeForm]);

  const commentElement = (
    <div className={props.passedChallenge ? classes.comment : classes.commentFailed}>
      <b>Feedback for this task:</b> {props.feedbackComment}
    </div>
  );

  let diffViewer;
  if (parentField && isShownAsMiniform) {
    const formAnnotation = annotations[parentField.field_id];
    diffViewer = (
      <div className={classes.diffMiniform}>
        <div className={classes.scroll} ref={scrollRef}>
          {props.feedbackComment && commentElement}
          <DiffTree
            fields={[parentField]}
            response={{
              annotations: {
                [parentField.field_id]: formAnnotation,
              },
            }}
            showEmptyDiffMessage={true}
            initialExpandedNodeIds={findNodesToExpand(fields, annotations)}
            projectId={props.project}
          />
        </div>
        <FormFieldNav
          formFields={fields as FormField[]}
          activeFieldId={parentField.field_id}
          setBreadcrumbs={setBreadcrumbs}
          inaccurateFieldIds={inaccurateForms.map(form => form.field_id)}
        />
      </div>
    );
  } else {
    diffViewer = (
      <div className={classNames(classes.diffRegular)}>
        {props.feedbackComment && commentElement}
        <DiffTree
          fields={fields}
          response={props.graderDiffResponse}
          isCompToPrevious={props.responseKey === DiffView.StepDiff}
          projectId={props.project}
        />
      </div>
    );
  }

  return (
    <DocumentProvider
      attachments={attachments.length}
      ocrTextFragments={ocrTextFragments}
      zIndex={100002}
      fields={fields}
    >
      <div className={classes.layer}>
        <div className={classNames(classes.layout)} style={{ zIndex: 100001 }}>
          {(!mobile || !viewingAnswerKey) && (
            <AttachmentCarousel
              attachments={attachments}
              activeForm={activeFormForAttachment}
              loggedUserId={props.loggedUserId}
              useProtectedText={props.useProtectedText}
              project={props.project}
            />
          )}
          {diffViewer}
        </div>
        <Button
          className={classes.toggleViewButton}
          primary
          onClick={() => setViewingAnswerKey(v => !v)}
        >
          View {viewingAnswerKey ? 'Attachment' : 'Answer'}
        </Button>
      </div>
    </DocumentProvider>
  );
};

const useAnswerKeyStyles = makeStyles({
  layer: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  layout: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    [`@media (max-width: ${MOBILE_THRESHOLD})`]: {
      display: 'block',
    },
  },
  diffRegular: {
    maxWidth: 460,
    width: '100%',
    flexShrink: 1,
    overflowY: 'auto',
    boxShadow: '0 50px 100px -30px rgba(0, 0, 0, .5)',
    [`@media (max-width: ${MOBILE_THRESHOLD})`]: {
      width: '100%',
    },
  },
  diffMiniform: {
    width: 460,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  scroll: {
    flexGrow: 1,
    overflowY: 'scroll',
  },

  comment: {
    padding: '8px',
    fontSize: '14px',
    border: '1px solid #19b629',
    borderRadius: '8px',
    margin: '12px',
  },
  commentFailed: {
    padding: '8px',
    fontSize: '14px',
    border: '1px solid #ec322f',
    borderRadius: '8px',
    margin: '12px',
  },
  label: {
    fontSize: 14,
    lineHeight: '20px',
    color: '#808080',
  },
  toggleViewButton: {
    display: 'none',
    position: 'absolute',
    zIndex: 1000000,
    bottom: '16px',
    left: '50%',
    transform: 'translateX(-50%)',
    [`@media (max-width: ${MOBILE_THRESHOLD})`]: {
      display: 'block',
    },
  },
});

// Only applies to miniform diffs
const isInaccurateForm = (
  form: TextCollectionField,
  annotations: Record<string, TextCollectionGraderAnnotation>,
): boolean => {
  const formAnnotation = annotations[form.field_id];
  if (!formAnnotation) {
    return false;
  }
  if (isFieldSetGraderAnnotation(formAnnotation)) {
    return !!formAnnotation.response.find(
      part => part.result !== GraderResult.Matched || !part.hasCorrectChildren,
    );
  }
  const graderValues = formAnnotation.response as GraderValue<TextCollectionUnitValueType>[];
  return !!graderValues.find(value => value.result !== GraderResult.Correct);
};

const findNodesToExpand = (
  fields: TextCollectionField[],
  annotations: Record<string, TextCollectionGraderAnnotation>,
): string[] => {
  let initExpandedNodes: string[] = [];
  for (const field of fields) {
    const annotation = annotations[field.field_id];
    if (annotation && isFieldSetGraderAnnotation(annotation)) {
      const expandedNodeIds = annotation.response.map((_, index) => {
        return pathJoin([{ id: field.field_id, index }]);
      });
      initExpandedNodes = initExpandedNodes.concat(expandedNodeIds);
    }
  }
  return initExpandedNodes;
};

export default TextCollectionAnswerKey;
